import React, { useEffect, useState } from 'react';
import { Bot, FileText, Loader2, Mic, RotateCcw, Trash2 } from 'lucide-react';
import type { TrashedMeetingDTO } from '../global';
import type { ToastKind } from './Toast';
import { useAsyncData } from '../features/directory/useAsyncData';
import { TRASH_RETENTION_DAYS } from '../features/meetings/DeleteMeetingDialog';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';
import { cardClass, formatDate, pageTitle } from './ui/styles';
import { plural, t } from '../lib/i18n';

interface TrashViewProps {
  isDarkMode: boolean;
  onNotify: (kind: ToastKind, text: string) => void;
}

/** Kalıcı silmeye kaç gün kaldı (en az 0). */
function daysLeft(purgeAtISO: string): number {
  return Math.max(0, Math.ceil((Date.parse(purgeAtISO) - Date.now()) / 86_400_000));
}

function durationLabel(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  return t(`${minutes} dk`, `${minutes} min`);
}

const SourceIcon: React.FC<{ source: string }> = ({ source }) => {
  const Icon = source === 'bot' ? Bot : source === 'desktop' ? Mic : FileText;
  return <Icon className="w-4 h-4 text-neutral-500 shrink-0" />;
};

/**
 * Çöp kutusu — silinen toplantılar 30 gün burada durur, sonra sunucunun gece
 * temizliğiyle dosyalarıyla birlikte kalıcı silinir. Buradan geri alınabilir
 * ya da hemen kalıcı silinebilir (geri dönüşü yok).
 */
export const TrashView: React.FC<TrashViewProps> = ({ isDarkMode, onNotify }) => {
  const trash = useAsyncData(() => window.zabit.listTrash(), 'trash');
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  /** Kalıcı silme onayı bekleyenler: tek toplantı ya da "boşalt" için hepsi. */
  const [purgeTarget, setPurgeTarget] = useState<TrashedMeetingDTO[] | null>(null);

  // Başka bir yerde toplantı silinince/geri alınınca liste tazelensin.
  useEffect(() => window.zabit.onMeetingsChanged(() => void trash.reload(true)), [trash.reload]);

  const items = trash.data ?? [];

  const withBusy = async (ids: string[], work: () => Promise<void>) => {
    setBusyIds((prev) => new Set([...prev, ...ids]));
    try {
      await work();
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      await trash.reload(true);
    }
  };

  const restore = (item: TrashedMeetingDTO) =>
    withBusy([item.id], async () => {
      try {
        await window.zabit.restoreMeetings([item.id]);
        onNotify('success', t(`“${item.title}” geri alındı.`, `“${item.title}” was restored.`));
      } catch (err) {
        onNotify('error', (err as Error).message);
      }
    });

  const purge = (targets: TrashedMeetingDTO[]) => {
    setPurgeTarget(null);
    const ids = targets.map((item) => item.id);
    return withBusy(ids, async () => {
      try {
        const purged = await window.zabit.purgeMeetings(ids);
        onNotify(
          'success',
          purged.length === 1
            ? t('Toplantı kalıcı olarak silindi.', 'The meeting was permanently deleted.')
            : t(`${purged.length} toplantı kalıcı olarak silindi.`, `${plural(purged.length, 'meeting', 'meetings')} permanently deleted.`)
        );
      } catch (err) {
        onNotify('error', (err as Error).message);
      }
    });
  };

  const rowButton = `flex items-center gap-1.5 text-[12.5px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed`;

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-6 select-none">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className={pageTitle(isDarkMode)}>{t('Çöp kutusu', 'Trash')}</h1>
          <p className="text-[13px] text-neutral-500 mt-1.5">
            {t(
              `Sildiğin toplantılar ${TRASH_RETENTION_DAYS} gün burada durur, sonra kaydı, transkripti ve notlarıyla birlikte kendiliğinden kalıcı olarak silinir. Buradan silersen hemen ve geri dönüşsüz silinir.`,
              `Meetings you delete stay here for ${TRASH_RETENTION_DAYS} days, then they’re permanently deleted with their recording, transcript and notes. Deleting from here is immediate and can’t be undone.`
            )}
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={() => setPurgeTarget(items)}
            disabled={busyIds.size > 0}
            className={`${rowButton} shrink-0 text-rose-400 hover:bg-rose-500/10`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('Çöp kutusunu boşalt', 'Empty trash')}
          </button>
        )}
      </div>

      {trash.isLoading && (
        <p className="text-[13px] text-neutral-500 flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('Yükleniyor…', 'Loading…')}
        </p>
      )}
      {trash.error && <p className="text-[13px] text-rose-400">{trash.error}</p>}

      {!trash.isLoading && !trash.error && items.length === 0 && (
        <div className="text-center py-12 space-y-2">
          <Trash2 className="w-8 h-8 text-neutral-600 mx-auto" />
          <p className="text-[13px] text-neutral-500">{t('Çöp kutusu boş.', 'The trash is empty.')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className={`rounded-2xl border divide-y ${cardClass(isDarkMode)} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
          {items.map((item) => {
            const busy = busyIds.has(item.id);
            const left = daysLeft(item.purgeAtISO);
            return (
              <div key={item.id} className="flex items-center justify-between gap-3 px-4 py-3.5">
                <div className="flex items-center gap-3 min-w-0">
                  <SourceIcon source={item.source} />
                  <div className="min-w-0">
                    <div className={`text-[14px] font-semibold truncate ${isDarkMode ? 'text-neutral-200' : 'text-neutral-900'}`}>
                      {item.title}
                    </div>
                    <div className="text-[12px] text-neutral-500 truncate">
                      {[
                        item.recordedAtISO ? formatDate(item.recordedAtISO) : null,
                        item.durationSeconds > 0 ? durationLabel(item.durationSeconds) : null,
                        left === 0
                          ? t('bu gece kalıcı silinecek', 'permanently deleted tonight')
                          : t(`${left} gün sonra kalıcı silinecek`, `permanently deleted in ${plural(left, 'day', 'days')}`),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-500 mx-3" />
                  ) : (
                    <>
                      <button
                        onClick={() => void restore(item)}
                        className={`${rowButton} ${isDarkMode ? 'text-neutral-300 hover:bg-neutral-700/40' : 'text-neutral-700 hover:bg-neutral-100'}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {t('Geri al', 'Restore')}
                      </button>
                      <button
                        onClick={() => setPurgeTarget([item])}
                        title={t('Kalıcı olarak sil', 'Delete permanently')}
                        className={`${rowButton} text-rose-400 hover:bg-rose-500/10`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t('Kalıcı sil', 'Delete forever')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={purgeTarget !== null}
        onClose={() => setPurgeTarget(null)}
        title={
          purgeTarget && purgeTarget.length > 1
            ? t(`${purgeTarget.length} toplantı kalıcı olarak silinsin mi?`, `Permanently delete ${purgeTarget.length} meetings?`)
            : t('Kalıcı olarak silinsin mi?', 'Delete permanently?')
        }
        className="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPurgeTarget(null)}>
              {t('Vazgeç', 'Cancel')}
            </Button>
            <Button onClick={() => purgeTarget && void purge(purgeTarget)} className="bg-rose-600 text-white hover:bg-rose-500">
              <Trash2 className="size-4" />
              {t('Kalıcı olarak sil', 'Delete permanently')}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm leading-relaxed">
          {purgeTarget?.length === 1 && <p className="font-medium break-words">“{purgeTarget[0].title}”</p>}
          <p className="text-muted-foreground">
            {t(
              'Ses kaydı, transkript, özet ve notlar hemen silinir. Bu işlem geri alınamaz.',
              'The recording, transcript, summary and notes are deleted right away. This can’t be undone.'
            )}
          </p>
        </div>
      </Dialog>
    </div>
  );
};
